(function (scope) {
  'use strict';

  const MATERIAL_PLA = 'PLA binnen';
  const MATERIAL_PETG = 'PETG buiten';
  const minimumCents = 1795;
  const rateVersion = 'shared-text-production-2026-08-27';
  const houseNumberRateVersion = 'house-number-normal-2026-08-25';

  const wallTextRates = Object.freeze({
    8: Object.freeze({ [MATERIAL_PLA]: 68, [MATERIAL_PETG]: 85 }),
    12: Object.freeze({ [MATERIAL_PLA]: 68, [MATERIAL_PETG]: 86 }),
    16: Object.freeze({ [MATERIAL_PLA]: 77, [MATERIAL_PETG]: 96 }),
    20: Object.freeze({ [MATERIAL_PLA]: 89, [MATERIAL_PETG]: 111 }),
  });

  const houseNumberRates = Object.freeze({
    10: Object.freeze({
      [MATERIAL_PETG]: Object.freeze({ fullCharacter: 895, smallerSuffix: 597 }),
    }),
    20: Object.freeze({
      [MATERIAL_PETG]: Object.freeze({ fullCharacter: 1395, smallerSuffix: 930 }),
    }),
    30: Object.freeze({
      [MATERIAL_PETG]: Object.freeze({ fullCharacter: 1873, smallerSuffix: 1249 }),
    }),
  });

  const mountingCents = Object.freeze({
    onlyProduct: 0,
    selfAdhesiveIndoor: 995,
    selfAdhesiveOutdoor: 1295,
    adhesiveKitRoughSurface: 2495,
  });

  function requireRate(rates, height, material) {
    if (!Number.isInteger(height) || !Object.hasOwn(rates, height)) {
      throw new RangeError('Ongeldige pilothoogte.');
    }
    if (typeof material !== 'string' || !Object.hasOwn(rates[height], material)) {
      throw new RangeError('Ongeldig pilotmateriaal.');
    }
    return rates[height][material];
  }

  function paidWidth(actualWidthCm) {
    if (typeof actualWidthCm !== 'number' || !Number.isFinite(actualWidthCm) || actualWidthCm <= 0) {
      throw new RangeError('Werkelijke breedte moet een positief eindig getal zijn.');
    }
    return Math.ceil(actualWidthCm);
  }

  function calculateWallText(actualWidthCm, height, material) {
    return calculateTextProductionPrice({
      actualWidthCm,
      heightCm: height,
      thicknessMm: 10,
      material,
      fullCharacters: 0,
      hasSmallerSuffix: false,
    });
  }

  function validateComponentCounts(fullCharacters, hasSmallerSuffix) {
    if (!Number.isInteger(fullCharacters) || fullCharacters < 0 || fullCharacters > 6) {
      throw new RangeError('Aantal volledig berekende huisnummertekens moet tussen 0 en 6 liggen.');
    }
    if (typeof hasSmallerSuffix !== 'boolean') {
      throw new TypeError('De toevoegingsletterstatus moet waar of onwaar zijn.');
    }
    const printedCharacters = fullCharacters + (hasSmallerSuffix ? 1 : 0);
    if (printedCharacters < 1 || printedCharacters > 6) {
      throw new RangeError('Aantal geprinte huisnummertekens moet tussen 1 en 6 liggen.');
    }
    return printedCharacters;
  }

  function calculateMeasuredWidthPrice(actualWidthCm, heightCm, thicknessMm, material) {
    if (thicknessMm !== 10) return null;
    const heightRates = wallTextRates[heightCm];
    if (!heightRates || !Object.hasOwn(heightRates, material)) return null;
    const rateCentsPerCm = heightRates[material];
    const paidWidthCm = paidWidth(actualWidthCm);
    const subtotalCents = paidWidthCm * rateCentsPerCm;
    const totalCents = Math.max(minimumCents, subtotalCents);

    return Object.freeze({
      calculationBasis: 'measured-width',
      actualWidthCm,
      paidWidthCm,
      heightCm,
      thicknessMm,
      material,
      rateCentsPerCm,
      rateCentsPerCharacter: null,
      smallerSuffixRateCents: null,
      suffixCents: 0,
      subtotalCents,
      minimumCents,
      minimumApplied: subtotalCents < minimumCents,
      totalCents,
      rateVersion,
    });
  }

  function calculateComponentPrice(fullCharacters, hasSmallerSuffix, heightCm, thicknessMm, material) {
    const printedCharacters = validateComponentCounts(fullCharacters, hasSmallerSuffix);
    const expectedThickness = { 10: 5, 20: 10, 30: 15 }[heightCm];
    if (thicknessMm !== expectedThickness) {
      throw new RangeError('Ongeldige combinatie van hoogte en dikte.');
    }

    const rates = requireRate(houseNumberRates, heightCm, material);
    const suffixCents = hasSmallerSuffix ? rates.smallerSuffix : 0;
    const subtotalCents = (fullCharacters * rates.fullCharacter) + suffixCents;
    const totalCents = Math.max(minimumCents, subtotalCents);

    return Object.freeze({
      calculationBasis: 'production-components',
      printedCharacters,
      fullCharacters,
      hasSmallerSuffix,
      actualWidthCm: null,
      paidWidthCm: null,
      heightCm,
      thicknessMm,
      material,
      rateCentsPerCm: null,
      rateCentsPerCharacter: rates.fullCharacter,
      smallerSuffixRateCents: rates.smallerSuffix,
      suffixCents,
      subtotalCents,
      minimumCents,
      minimumApplied: subtotalCents < minimumCents,
      totalCents,
      rateVersion,
    });
  }

  function calculateTextProductionPrice(specification) {
    if (!specification || typeof specification !== 'object' || Array.isArray(specification)) {
      throw new TypeError('Productiespecificatie ontbreekt.');
    }
    const {
      actualWidthCm,
      heightCm,
      thicknessMm,
      material,
      fullCharacters = 0,
      hasSmallerSuffix = false,
    } = specification;
    if (!Number.isInteger(heightCm) || !Number.isFinite(thicknessMm) || thicknessMm <= 0) {
      throw new RangeError('Ongeldige productieafmetingen.');
    }
    if (typeof material !== 'string') throw new TypeError('Productiemateriaal ontbreekt.');
    if (typeof hasSmallerSuffix !== 'boolean') {
      throw new TypeError('De toevoegingsletterstatus moet waar of onwaar zijn.');
    }

    // Een verkleinde toevoegingsletter is een afzonderlijke productiecomponent.
    // Uniforme tekst met een goedgekeurd centimetertarief gebruikt altijd de
    // werkelijk gemeten breedte, ongeacht uit welke configurator deze komt.
    if (!hasSmallerSuffix) {
      const measured = calculateMeasuredWidthPrice(actualWidthCm, heightCm, thicknessMm, material);
      if (measured) return measured;
    }
    return calculateComponentPrice(fullCharacters, hasSmallerSuffix, heightCm, thicknessMm, material);
  }

  function calculateHouseNumber(
    fullCharacters,
    hasSmallerSuffix,
    height,
    material,
    actualWidthCm = null,
    thicknessMm = { 10: 5, 20: 10, 30: 15 }[height],
  ) {
    return calculateTextProductionPrice({
      actualWidthCm,
      heightCm: height,
      thicknessMm,
      material,
      fullCharacters,
      hasSmallerSuffix,
    });
  }

  const pricing = Object.freeze({
    rateVersion,
    houseNumberRateVersion,
    wallTextRates,
    houseNumberRates,
    minimumCents,
    mountingCents,
    paidWidth,
    calculateTextProductionPrice,
    calculateWallText,
    calculateHouseNumber,
  });

  Object.defineProperty(scope, 'DuneSignSoftLaunchPricing', {
    value: pricing,
    enumerable: true,
    configurable: false,
    writable: false,
  });
})(typeof window === 'undefined' ? globalThis : window);
